import { Injectable } from '@angular/core';
import { MessageService } from './message.service';

const TOAST_GAP_MS = 1500;

export interface ThrottleInfo {
	penalty: number | null;
	rule: number | null;
	ruleText: string | null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
	if (!value || typeof value !== 'object') return null;
	return value as Record<string, unknown>;
}

function positiveNumber(value: unknown): number | null {
	if (value == null || value === '') return null;
	const seconds = typeof value === 'number' ? value : Number(value);
	if (!Number.isFinite(seconds) || seconds <= 0) return null;
	return Math.ceil(seconds);
}

/** Penalty wait plus the throttle rule, from a callable error or an HTTP 429 body. */
export function readThrottleInfo(error: unknown): ThrottleInfo {
	const record = asRecord(error);
	const details = asRecord(record?.['details']) ?? asRecord(record?.['customData']) ?? asRecord(record?.['error']);
	const header = (record?.['headers'] as { get?: (name: string) => string | null } | undefined)?.get?.('Retry-After');

	return {
		penalty: positiveNumber(details?.['penalty']) ?? positiveNumber(details?.['retryAfterSeconds']) ?? positiveNumber(header),
		rule: positiveNumber(details?.['rule']),
		ruleText: typeof details?.['ruleText'] === 'string' && details['ruleText'] ? details['ruleText'] : null
	};
}

@Injectable({ providedIn: 'root' })
export class ThrottleNotice {
	private lastShownAt = 0;

	constructor(private messageService: MessageService) {}

	show(info: ThrottleInfo): void {
		const now = Date.now();
		if (now - this.lastShownAt < TOAST_GAP_MS) return;
		this.lastShownAt = now;

		const wait =
			info.penalty != null
				? `Patientez ${info.penalty} ${info.penalty === 1 ? 'seconde' : 'secondes'} avant de réessayer.`
				: 'Patientez avant de réessayer.';
		const limit = info.ruleText ? `Vous avez atteint la limite. ${info.ruleText}.` : 'Vous avez atteint la limite.';
		const content = `${limit} ${wait} Si vous réessayez avant la fin de ce délai, l'attente sera prolongée.`;
		this.messageService.show(content, 'error', 10000);
	}
}
